import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { WebComponent } from './app/web.component';
import {
  ApplicationConfig,
  EnvironmentProviders,
  ErrorHandler,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  ModuleWithProviders,
  provideAppInitializer,
  Provider,
  provideZoneChangeDetection,
  Type
} from '@angular/core';
import { PreloadAllModules, provideRouter, RouterModule, withPreloading, withRouterConfig } from '@angular/router';
import { WEB_ROUTES, WebRoutingModule } from './app/web-routing.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ApiModule } from '@shared/api/api-loop/api.module';
import { LogModule } from '@shared/api/log-loop/api.module';
import { ContactsPipesModule } from '@shared/modules/contacts/components/pipes/contacts-pipes.module';
import { BrowserComponentModule } from '@shared/ui/browser-component.module';
import { ModalFormModule } from '@shared/forms/shell/modal-form/modal-form.module';
import { BrowserConfirmModule } from '@shared/shared/confirm/browser/browser-confirm/browser-confirm.module';
import { BrowserStorageModule } from '@shared/modules/storage/data-access/browser-storage.module';
import { IntegrationsHelperModule } from '@shared/services/integrations/helper/integrations-helper.module';
import { FancyDateModule } from '@shared/shared/fancy-date/shell/fancy-date.module';
import { ContactActionsModule } from '@shared/modules/contacts/data-access/contact-actions/contact-actions.module';
import { BrowserViewportModule } from '@shared/shared/viewport/browser/browser-viewport.module';
import { BrowserDrawersModule } from '@shared/shared/drawers/browser/browser-drawers/browser-drawers.module';
import { AuthGuard } from '@dta/ui/guards/auth.guard';
import { KeyboardShortcutsListenerService } from '@dta/ui/services/keyboard-shortcuts-listener/keyboard-shortcuts-listener.service';
import { StorageService } from '@dta/shared/services/storage/storage.service';
import { DesktopNotificationsService } from '@dta/ui/services/desktop-notifications/desktop-notifications.service';
import { LightBackgroundService } from '@shared/services/light-background.service/light-background.service';
import { CreateChannelFormDialogService } from '@shared/services/create-channel-form-dialog/create-channel-form-dialog.service';
import { FlushDatabaseService } from '@shared/services/flush-db/flush-db.service';
import { ChannelSetupService } from '@shared/services/data/channel-inbox-setup/channel-setup.service';
import { SyncSettingService } from '@shared/services/data/sync-settings/sync-settings.service';
import { FileApiCacheService } from '@shared/modules/files/shell/file-api-cache/file-api-cache.service';
import { BaseApiCacheService } from '@shared/api/api-cache/base-api-cache.service';
import { StorageProviderService } from '@shared/cache/storage-provider/storage-provider';
import { TrackingService } from '@shared/modules/tracking/tracking.service';
import { TrackingServiceWeb } from './app/services/tracking/tracking.service.web';
import {
  DROPDOWN_CLICK_TRACKER,
  DropdownClickTracker
} from '@shared/ui/dropdown/injection-tokens/dropdown-click-tracker';
import { WebOnboardingService } from '@shared/services/web-onboarding/web-onboarding.service';
import { WebOnboardingServiceWeb } from './app/services/web-onboarding/web-onboarding.service.web';
import { FileDownloadService } from '@shared/modules/files/shell/services/file-download.service';
import { BrowserFileDownloadService } from '@shared/modules/files/shell/services/browser-file-download.service';
import { WatchdogService } from '@shared/services/watchdog/watchdog.service';
import { HttpEventService } from '@shared/interceptors/http-event.service';
import { AppLoadService } from '@shared/modules/app/components/app-load.service';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { initApp } from '@shared/modules/app/helpers/init-app';
import { provideNavigationErrorRecovery } from '@shared/modules/app/providers/navigation-error-recovery.provider';
import { INTERCEPTOR_PROVIDERS } from '@shared/app-runner/providers/interceptors';
import { CommentIframeActionsModule } from '@shared/modules/comments/services/comment-iframe-actions/comment-iframe-actions.module';
import { runApplication } from '@shared/app-runner/web/run-application';
import { LoginDialogService } from '@dta/ui/components/common/login-dialog/login-dialog.service';
import { ContactDetailHelperService } from '@shared/services/contact-detail-helper/contact-detail-helper.service';
import { OAuthService } from '@shared/services/oauth/oauth.service';
import { ElectronService } from '@shared/services/electron/electron';
import { ElectronServiceWeb } from './app/services/electron/electron.service.web';
import { LocationService } from '@shared/shared/navigation/location.service';
import { LocationServiceWeb } from './app/services/navigation/location.service.web';
import { DeepLinksService } from '@shared/modules/deeplinks/common/deeplinks.service';
import { DeepLinksServiceWeb } from '@shared/modules/deeplinks/web/deeplinks.service.web';
import { LoginService } from '@shared/services/login/login.service';
import { LoginServiceWeb } from './app/services/login/login-service.web';
import { TokenType, TokenTypeValue } from '@shared/shared/auth/common/constants/token-type';
import { AUTH_TOKENS_STORAGE_CONFIGURATION } from '@shared/shared/auth/data-access/auth-tokens-storage-configuration';
import { AuthDataAccessModule } from '../brisket/modules/auth/auth-data-acess.module';
import { SomeoneIsTypingService } from '@dta/ui/services/someone-is-typing/someone-is-typing.service';
import { CollisionDetectionService } from '@dta/ui/services/collision-detection/collision-detection.service';
import { CustomErrorHandler } from '@shared/shared/errors/error-handler';
import { environment } from './environments/environment';
import { ENVIRONMENT } from '@shared/environments/model';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { DevCommandApiInterceptor } from '@shared/interceptors/dev-command-api.interceptor';

const authTokensStorageConfiguration: Record<TokenTypeValue, string> = {
  [TokenType.access]: 'LOOP_FRONTEND_ACCESS_TOKEN',
  [TokenType.refresh]: 'LOOP_FRONTEND_REFRESH_TOKEN'
};

const WEB_APP_IMPORTS: (Type<any> | ModuleWithProviders<any>)[] = [
  BrowserModule,
  WebRoutingModule,
  BrowserAnimationsModule,
  RouterModule,
  ApiModule,
  LogModule,
  ContactsPipesModule,
  BrowserComponentModule.forRoot(),
  ModalFormModule.forRoot(),
  BrowserConfirmModule.forRoot(),
  BrowserStorageModule.forRoot(),
  IntegrationsHelperModule.forRoot(),
  FancyDateModule.forRoot(),
  ContactActionsModule.forRoot(),
  BrowserViewportModule.forRoot(),
  BrowserDrawersModule.forRoot(),
  CommentIframeActionsModule.forRoot(),
  AuthDataAccessModule.forRoot()
];

// TODO Unify imports from all windows
const WEB_APP_PROVIDERS: (Provider | EnvironmentProviders)[] = [
  provideHttpClient(withInterceptorsFromDi()),
  provideAppInitializer(() => initApp(inject(AppLoadService))()),
  AuthGuard,
  KeyboardShortcutsListenerService,
  StorageService,
  DesktopNotificationsService,
  LightBackgroundService,
  CreateChannelFormDialogService,
  FlushDatabaseService,
  ChannelSetupService,
  SyncSettingService,
  FileApiCacheService,
  BaseApiCacheService,
  StorageProviderService,
  SomeoneIsTypingService,
  CollisionDetectionService,
  { provide: TrackingService, useClass: TrackingServiceWeb },
  {
    provide: DROPDOWN_CLICK_TRACKER,
    useFactory: (trackingService: TrackingService): DropdownClickTracker => ({
      track: (location: string, action: string) => trackingService.trackUserClick(location, action)
    }),
    deps: [TrackingService]
  },
  { provide: WebOnboardingService, useClass: WebOnboardingServiceWeb },
  { provide: FileDownloadService, useClass: BrowserFileDownloadService },

  //REFACTOR
  LoginDialogService,
  ContactDetailHelperService,
  OAuthService,
  { provide: ElectronService, useClass: ElectronServiceWeb },
  { provide: LocationService, useClass: LocationServiceWeb },
  { provide: DeepLinksService, useClass: DeepLinksServiceWeb },
  { provide: LoginService, useClass: LoginServiceWeb },
  { provide: AUTH_TOKENS_STORAGE_CONFIGURATION, useValue: authTokensStorageConfiguration },
  WatchdogService,
  HttpEventService,
  AppLoadService,
  {
    provide: ErrorHandler,
    useClass: CustomErrorHandler
  },
  { provide: ENVIRONMENT, useValue: environment }
];

const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(),
    provideRouter(
      WEB_ROUTES,
      withPreloading(PreloadAllModules),
      withRouterConfig({
        onSameUrlNavigation: 'reload'
      })
    ),
    provideNavigationErrorRecovery(),
    importProvidersFrom(WEB_APP_IMPORTS),
    ...WEB_APP_PROVIDERS,
    ...INTERCEPTOR_PROVIDERS,
    ...(environment.isProduction
      ? []
      : [{ provide: HTTP_INTERCEPTORS, useClass: DevCommandApiInterceptor, multi: true }]),
    { provide: LOCALE_ID, useValue: 'en' }
  ]
};

runApplication({
  bootstrapFn: () => bootstrapApplication(WebComponent, appConfig),
  isProduction: environment.isProduction
});
